<template>
  <div
    class="editor-section"
    :style="!multiPreview ? 'padding-right: 115px;' : 'padding-right: 10px;'"
    :class="{ 'ai-section': aiSection }"
  >
    <div :class="multiPreview ? 'section-name-multi' : 'section-name'">
      <div
        v-if="!multiPreview"
        @click="onClickSection"
        style="max-width: 65px; cursor: pointer"
        class="ellipsis d-inline-block"
        :title="sectionName"
        :class="{'standard-inactive': standardInactive}" 
      >
        {{ sectionName }}
      </div>
      

      <div v-if="aiSection" class="ai-section-icon">
        <span
          v-if="!multiPreview"
          class="hover-element"
          slot="icon"
          @mouseenter="hoverAi = true"
          @mouseleave="hoverAi = false"
          :class="templateSection.active ? 'ai-active' : 'text-muted'"
        >
          <i class="fa-light fa-bolt"></i>
        </span>
        <transition name="fade">
          <div v-if="hoverAi" class="hover-content">
            <div class="top">
              <h3>{{ $t("ai-sections.ai-section") }}</h3>
              <span class="openai-icon">
                <img src="@/assets/svg/openai.svg" alt="OpenAI logo" />
              </span>
            </div>
            <p class="description" v-html="$t('ai-sections.popup-description')"></p>
            <br />
            <p class="text-small" :class="templateSection.active ? 'ai-active' : 'text-muted'">
              {{
                templateSection.active ? $t("ai-sections.generate-status-on") : $t("ai-sections.generate-status-off")
              }}
            </p>
          </div>
        </transition>
      </div>
      <div
        :class="'format-label-icon'"
        :tooltip="multiPreview && showTooltips ? sectionName : ''"
        tooltip-placement="right"
      >
        <svg
          v-bind:class="{ fill: indicatorToggle }"
          @click="indicatorToggle = !indicatorToggle"
          width="12"
          height="8"
          viewBox="-1 0 14 8"
          fill="white"
          stroke-width="1.5px"
          stroke="#383838"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.14271 8H0.428564C0.191997 8 0 7.80089 0 7.55556V0.444444C0 0.199111 0.191997 0 0.428564 0H8.14271C8.25671 0 8.36556 0.0471111 8.44613 0.129778L11.8746 3.68533C12.0418 3.85867 12.0418 4.14044 11.8746 4.31378L8.44613 7.86933C8.36556 7.95289 8.25671 8 8.14271 8Z"
          />
        </svg>
      </div>
    </div>

    <div
      class="section-text"
      :style="multiPreview ? 'padding-right: 15px;' : ''"
      :class="{ noselect: isDemo, 'loading-overlay': loadingSection || aiSectionPreviewError }"
    >
      <span v-bind:class="{ hiddentext: indicatorToggle }"></span>
      <SentencePreviewHtml
        :class="{ texttoggle: indicatorToggle }"
        v-if="previewSection.sentences"
        :sentences="previewSection.sentences"
        :section-id="sectionId"
        :relevantVariables="relevantVariables"
        :showRawHTML="showRawHTML"
      />

      <span
        class="start-writing"
        v-bind:class="{ texttoggle: indicatorToggle }"
        v-else-if="!dummy"
        style="cursor:pointer"
        @click="onClickSection"
      >
        {{ aiSection ? $t("editor-section.config-ai"): $t("editor-section.dummy-desc") }}
      </span>
    </div>
    <div v-if="aiSectionPreviewError" class="ai-error">
      <span class="text-danger text-small">
        {{ $t('standard-error') }}
      </span> 
      <span class="error-btn text-small" @click="regenerateAiResponse()">
        <i class="fa-light fa-sync-alt"></i>
      </span> 
    </div>
    <div v-show="loadingSection">
      <i class="fa-light fa-circle-notch fa-spin loading-overlay-circle"></i>
    </div>

    <div class="actions" v-if="!multiPreview">
      <div
        v-if="!isDemo"
        class="section-action"
        :tooltip="showTooltips ? $t('duplicate-below') : ''"
        tooltip-placement="left"
        @click="duplicateSection()"
      >
        <i class="fa-light fa-clone"></i>
      </div>
      <dropdown
        left
        class="section-action"
        :tooltip="showTooltips ? $t('sentence.add-new') : ''"
        tooltip-placement="left"
        :disabled="isDemo"
      >
        <div slot="toggle" @click="handleAction($event, 'default')">
          <i class="fa-light fa-plus"></i>
        </div>
        <div class="dropdown-item" @click="$emit('create-above')">{{ $t("editor-section.create-above") }}</div>
        <div class="dropdown-item" @click="$emit('create-below')">{{ $t("editor-section.create-below") }}</div>
      </dropdown>

      <div
        @click="
          handleAction($event, 'default', () => {
            $emit('remove');
          })
        "
        :tooltip="showTooltips ? $t('editor-section.delete') : ''"
        tooltip-placement="left"
        class="section-action"
      >
        <i class="fa-light fa-trash"></i>
      </div>

      <div
        @click="
          handleAction($event, 'default', () => {
            onOptions();
          })
        "
        :tooltip="showTooltips ? $t('editor-section.options') : ''"
        tooltip-placement="left"
        class="section-action"
      >
        <!-- <i class="fa-light fa-pen"></i> -->
        <i class="fa-light fa-ellipsis-stroke-vertical"></i>
      </div>
      <div
        class="randomize-ai section-action"
        v-if="aiSection && hasPreview"
        :tooltip="showTooltips ? $t('ai-sections.regenerate-response') : ''"
        tooltip-placement="left"
        @click="regenerateAiResponse()"
      >
        <i class="fa-light fa-random"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { editorMixin } from "@/assets/js/editorMixin.js";
import SentencePreview from "@/assets/js/sentencePreview.js";
import EventBus from "@/assets/js/eventBus.js";
import SentencePreviewHtml from "@/components/SentencePreviewHtml";
import Dropdown from "@/components/Dropdown";
import RenameModal from "@/components/modals/RenameModal";
import SectionOptionsModal from "@/components/modals/SectionOptionsModal";
export default {
  mixins: [editorMixin],
  data() {
    return {
      indicatorToggle: false,
      editor: null,
      newlyEnteredText: "",
      previewHtml: [],
      cleanSection: {
        sectionName: this.sectionName,
        sectionId: this.sectionId,
        sectionText: ""
      },
      hoverAi: false
    };
  },
  props: {
    templateSection: {
      type: Object,
      required: true
    },
    previewSection: {
      type: Object,
      required: true
    },
    sectionId: {
      type: String,
      required: true
    },
    sectionName: {
      type: String,
      required: true
    },
    sectionType: {
      type: String
    },
    dummy: Boolean,
    multiPreview: {
      type: Boolean,
      default: false
    },
    relevantVariables: Array,
    showRawHTML: Boolean
  },
  watch: {
    previewSection(section) {
      // console.log(`section: ${JSON.stringify(section, null, 2)}`)
      this.initialize();
    }
  },
  methods: {
    duplicateSection() {
      this.$emit("clone-below", this.sectionId);
    },
    onClickSection() {
      this.indicatorToggle = false;
      EventBus.$emit("editSentence", { sentence: { path: [0, 0] }, element: null, sectionId: this.sectionId });
    },
    initialize() {
      if (this.dummy || !this.previewSection.sentences) return; // this is dummy section to fill out the remaining space in similar styling
      if (this.$refs.content) {
        while (this.$refs.content.firstChild) {
          this.$refs.content.removeChild(this.$refs.content.firstChild);
        }
        this.previewSection.sentences
          .map((s) => new SentencePreview(s, this.sectionId).getAsHtml())
          .forEach((html) => this.$refs.content.appendChild(html));
      }
      this.getTextForStats();
    },

    getTextForStats() {
      let fullText = this.previewSection.sentences
        .map((s) => new SentencePreview(s).getAsString())
        .join("")
        .replace(/<[^>]*>/g, "");
      this.cleanSection.sectionText = fullText;
      this.$emit("cleaned-section", this.cleanSection);
    },

    onOptions() {
      this.$modal.show(
        SectionOptionsModal,
        {
          templateSection: this.templateSection,
          onSave: (data) => {
            this.$set(this.templateSection, "name", data.name);
            this.$set(this.templateSection, "id", data.id);
            this.$set(this.templateSection, "paragraph", data.paragraph);
            this.$set(this.templateSection, "active", data.active);
            this.$store.state.unsavedChanges = true;
            this.$store
              .dispatch("getTemplatePreview", {
                template: this.$store.state.template,
                variables: this.$store.state.templateVariables
              })
              .then((preview) => {
                // this.$store.state.templatePreview = preview;
                this.$store.commit("setTemplatePreview", preview);
                this.randomized = false;
              });
          }
        },
        { width: 350, height: "auto" }
      );
    },
    regenerateAiResponse(){
      this.$store.dispatch('getTemplatePreviewForSection', this.sectionId)
    }
  },
  computed: {
    showTooltips() {
      return this.$store.state.showTooltips;
    },
    isDemo() {
      return this.$store.state.editorIsDemoVersion;
    },
    aiSection() {
      return this.sectionType == "ai";
    },
    loadingSection() {
      return this.$store.state.aiSectionsLoading.includes(this.sectionId);
    },
    hasPreview() {
      return this.$store.state.templatePreview.sections.some((s) => s.id == this.sectionId);
    },
    aiSectionPreviewError(){
      return this.$store.state.aiSectionPreviewError.includes(this.sectionId)
    },
    standardInactive(){
      return !this.aiSection && !this.templateSection.active
    }
    
    

  },
  mounted() {
    this.initialize();
  },
  components: {
    SentencePreviewHtml,
    Dropdown
  }
};
</script>

<style lang="less">
.editor-section {
  position: relative;
  display: flex;
  align-items: flex-start;

  // padding-right: 85px;
  &:hover {
    background-color: #fafafa;
  }

  .section-name {
    flex: 0 0 85px;
    height: 100%;
    padding: 17px 0 0 15px;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: bold;
    position: relative;
    width: 100%;

    .format-label-icon {
      cursor: pointer;
      width: 20px;
      position: absolute;
      right: -16px;
      top: 16px;
      z-index: 1;
    }


    .standard-inactive{
      opacity: .2;
    }
  }
  .section-name-multi {
    // flex: 0 0 85px;
    padding: 17px 0 0 10px;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: bold;
    position: relative;

    .format-label-icon {
      cursor: pointer;
      width: 20px;
      position: absolute;
      right: -16px;
      top: 16px;
      z-index: 1;
    }
  }
  .section-text {
    position: relative;
    padding: 15px 0 15px 15px;
    border-left: 1px solid #d8d8d8;

    .start-writing {
      font-style: italic;
    }
  }

  .actions {
    display: none;
    position: absolute;
    top: 13px;
    right: 6px;
  }
  &:hover .actions {
    // display: flex;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;

    .randomize-ai {
      grid-column-start: 4;
    }
  }
  .section-action {
    cursor: pointer;
    width: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    &:hover {
      border-radius: 50% 50%;
      background-color: var(--alabaster);
    }
  }

  .section-editor {
    flex: 1 1 auto;
    padding: 15px 0 0 15px;
    border-left: 1px solid #d8d8d8;

    .ql-editor {
      overflow-y: inherit !important;
      padding: 0;
      font-size: 14px;
      line-height: 24px;
    }

    span.preview-sentence-blot {
      &[depth] {
        /* background-color: rgba(0, 0, 0, 0.04); */
        cursor: pointer;
      }

      &.hovered {
        outline: 1px solid black;
        position: relative;
      }

      &.previewing {
        outline: 1px solid var(--orange-web);
        position: relative;
      }
    }
  }

  .fill {
    fill: #d8d8d8;
    stroke: #d8d8d8;
  }
  .hiddentext {
    display: block;
    &:before {
      content: "...";
    }
  }
  .aisection {
    font-size: 20px !important;
  }
  .texttoggle {
    display: none;
  }
  .noselect {
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
  }

  &.ai-section {
    background: var(--alabaster-40);
    &:hover {
      background: var(--alabaster);
    }
    .section-text {
      font-family: "Courier";
      .start-writing {
        font-style: unset;
      }
    }

    .ai-section-icon {
      margin: 8px 0;
      position: relative; 
    }
    .hover-element {
      padding: 8px;
      padding-left: 0;
      svg {
        font-size: 18px;
      }
    }
    .ai-error{
      position: absolute;
      top: 40px;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #fff;
      padding: 12px 20px;
      box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
      border-radius: var(--standard-border-radius);
    }
    .error-btn{
      cursor: pointer;
      text-decoration: underline;
      color: var(--orange-web);
      color: var(--charcoal);
    }
    .ai-active {
      color: var(--ai-secondary);
    }
    .hover-content {
      text-transform: none;
      width: 250px;
      position: absolute;
      z-index: 999;
      box-shadow: 2px 4px 4px 2px rgba(0, 0, 0, 0.25);
      background: var(--background-white);
      padding: 12px;
      border-radius: 0 10px 10px 10px;
      transform: translateX(25px);
      .top {
        display: flex;
        justify-content: space-between;
        .openai-icon img {
          width: 14px;
        }
      }
      p {
        margin: 0;
        &.description {
          font-weight: normal;
          font-size: 14px;
        }
      }
    }
  }
}
</style>
